import React, { useEffect, useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import klogo from '../../../../assets/Klogo.svg';
import generalStyles from '../../../../assets/stylesheets/generalStyles';
import Icon from '../../../../components/Icon';
import useUsers from '../../../../hooks/useUsers';
import usePosOrder from '../../../../hooks/usePosOrder';
import keyHasPermission from '../../../../helpers/keyHasPermission';
import useProfile from '../../../../hooks/useProfile';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polyline,
} from '@react-google-maps/api';
import LoadingState from '../../../../components/LoadingState';
import CourierPin from '../../../../assets/Courier.png';
import RestoranPın from '../../../../assets/Restoran.png';
import courierLive from '../../../../assets/courierLive.png';

import Pusher, { Channel } from 'pusher-js';

interface Props {
  open: boolean;
  onClose: () => void;
  setAddPoolModal: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setDelivererModal: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  activeDelivererId?: string | null;
  order: any | null;
  orderId: string;
  handleSetStatus: (
    table_id: string,
    status_id: number,
    isPickedUp?: boolean,
  ) => void;
  statusId?: number;
}

function DelivererModal({
  open,
  onClose,
  order,
  setAddPoolModal,
  activeDelivererId,
  setDelivererModal,
  orderId,
  handleSetStatus,
  statusId,
}: Props) {
  const { t } = useTranslation('orders');
  const { enqueueSnackbar } = useSnackbar();
  const { meProfile } = useProfile();
  const { fetchUsers, users, userLoading } = useUsers();
  const { setDeliverer } = usePosOrder();
  const [delivererId, setDelivererId] = useState(
    activeDelivererId || users[0]?.id || '',
  );
  let pusher: Pusher | null = null;
  let orderChannel: Channel | null = null;
  const [courierLat, setCourierLat] = useState<number | null>(null);
  const [courierLng, setCourierLng] = useState<number | null>(null);

  const hasSetDelivererPermission = keyHasPermission(
    'set-order-transaction-deliverer',
    meProfile?.permissions || [],
  );

  const handleClose = () => {
    onClose();
    setDelivererId('');
  };

  useEffect(() => {
    if (open) {
      fetchUsers({ page: 1, role: 'deliverer' });
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (activeDelivererId) {
        setDelivererId(activeDelivererId);
      } else {
        setDelivererId(users[0]?.id || '');
      }
    }
  }, [users, open]);

  const mapContainerStyle = {
    width: '100%',
    height: '400px',
  };

  const center = {
    lat: Number(order?.origin_lat) || 40.9828,
    lng: Number(order?.origin_lng) || 29.0534,
  };

  const handleCourierLocationUpdated = (r: any) => {
    if (
      r.courier.id === order?.deliverer.id ||
      r.courier.id === order.external_courier?.courier_location.courier_id
    ) {
      setCourierLat(r.courier_location.lat);
      setCourierLng(r.courier_location.lng);
    }
  };

  useEffect(() => {
    const appKey = process.env.REACT_APP_PUSHER_APP_KEY || '';

    // @ts-ignore
    pusher = new Pusher(appKey, {
      cluster: 'eu',
    });
    orderChannel = pusher.subscribe(`branch.${meProfile?.activeBranch?.id}`);

    if (orderChannel) {
      orderChannel.bind('courier.location.updated', r =>
        handleCourierLocationUpdated(r),
      );
    }

    return () => {
      if (orderChannel) {
        orderChannel.unbind(
          'courier.location.updated',
          handleCourierLocationUpdated,
        );
      }
    };
  }, [orderChannel]);

  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries: ['places', 'geometry'],
  });

  const decodedPath = order?.path
    ? google.maps.geometry.encoding.decodePath(order.path)
    : [];

  const handleMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    if (
      order?.origin_lat &&
      order?.origin_lng &&
      order?.destination_lat &&
      order?.destination_lng
    ) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({
        lat: Number(order.origin_lat),
        lng: Number(order.origin_lng),
      });
      bounds.extend({
        lat: Number(order.destination_lat),
        lng: Number(order.destination_lng),
      });
      map.fitBounds(bounds);
    }

    console.log('Map loaded and bounds set.');
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{ '& .MuiDialog-paper': { maxWidth: '750px' } }}
      fullWidth
    >
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">
          {`Sipariş No: ${order?.transaction_number} - ${order?.customer?.first_name}`}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography style={{ marginBottom: '20px' }}>
            {order?.customer_address?.address}
          </Typography>
          <div
            style={{
              display: 'flex',
              backgroundColor: '#808080', // Sol taraf (gri)
              borderRadius: '4px',
              overflow: 'hidden',
              width: '250px',
              height: '40px',
              marginBottom: '10px', // Genişlik görsele göre ayarlandı
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: '#808080',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '5px',
              }}
            >
              <Typography>{(order?.distance / 1000).toFixed()} km</Typography>
            </div>
            <div
              style={{
                flex: 1,
                backgroundColor: '#505050', // Sağ taraf (daha koyu gri)
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '5px',
              }}
            >
              <Typography>{(order?.duration / 60).toFixed(2)} dk</Typography>
            </div>
          </div>
        </div>

        {userLoading ? (
          <LoadingState />
        ) : isLoaded && order?.path ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={center}
            zoom={14}
            onLoad={handleMapLoad}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              gestureHandling: 'greedy',
            }}
          >
            {order?.origin_lat && order?.origin_lng && (
              <Marker
                position={{
                  lat: Number(order.origin_lat),
                  lng: Number(order.origin_lng),
                }}
                icon={{
                  url: RestoranPın,
                  scaledSize: new google.maps.Size(23, 30),
                }}
              />
            )}
            {order?.destination_lat && order?.destination_lng && (
              <Marker
                position={{
                  lat: Number(order.destination_lat),
                  lng: Number(order.destination_lng),
                }}
                icon={{
                  url: CourierPin,
                  scaledSize: new google.maps.Size(23, 30),
                }}
              />
            )}
            {courierLat && courierLng && (
              <Marker
                position={{
                  lat: courierLat,
                  lng: courierLng,
                }}
                icon={{
                  url: courierLive,
                  scaledSize: new google.maps.Size(23, 30),
                }}
              />
            )}
            {decodedPath.length > 0 && (
              <Polyline
                path={decodedPath}
                options={{ strokeColor: '#000000' }}
              />
            )}
          </GoogleMap>
        ) : (
          <LoadingState />
        )}
      </DialogContent>

      <DialogActions
        style={{ padding: '16px', width: '100%', display: 'flex', gap: '50px' }}
      >
        {order?.deliverer && order?.courier_type !== 'kuik' ? (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '500',
              mt: 1,
              textAlign: 'center',
            }}
          >
            Kurye: {order?.deliverer?.full_name}
          </Typography>
        ) : null}

        {order?.courier_type === 'kuik' ? (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Typography>
                <span style={{ fontWeight: 'bold' }}>Kurye</span>:{' '}
                {order?.external_courier?.name
                  .split(' ')
                  .map((word, index, arr) =>
                    index === arr.length - 1 ? word[0] + '****' : word,
                  )
                  .join(' ')}
              </Typography>
              <Typography>
                <span style={{ fontWeight: 'bold' }}>Telefon</span>:{' '}
                {order?.external_courier?.phone}
              </Typography>
            </div>
            <div>
              <Typography>Havuza Gönderildi</Typography>
            </div>
          </div>
        ) : null}
        {hasSetDelivererPermission &&
          (order?.status_id === 1 || order?.status_id === 2) &&
          order?.type_id === 3 &&
          order?.courier_type !== 'kuik' &&
          !order?.deliverer && (
            <Button
              startIcon={<i className="fas fa-motorcycle" />}
              style={{
                flex: 1,
                backgroundColor: '#000',
                color: '#fff',
                textTransform: 'none',
                marginRight: '8px',
                borderRadius: '8px',
              }}
              onClick={() => setDelivererModal(true)}
            >
              <IconButton sx={generalStyles.icon}>
                <Icon
                  name="TwoWheeler"
                  style={{ color: 'white', marginRight: '8px' }}
                />
              </IconButton>
              <div style={{ color: 'white', marginLeft: '8px' }}>
                Kuryeni seç
              </div>
            </Button>
          )}

        {meProfile?.branches?.[0]?.has_partner_plan !== false &&
          order?.courier_type !== 'kuik' &&
          order?.courier_type !== 'provider' &&
          !order?.deliverer &&
          order?.distance <
            (meProfile?.activeBranch?.partner_plans?.[0]
              ?.maximum_delivery_distance ?? Infinity) && (
            <Button
              variant="contained"
              onClick={() => setAddPoolModal(true)}
              startIcon={<i className="fas fa-share-square" />}
              style={{
                flex: 1,
                backgroundColor: '#0052cc',
                color: '#fff',
                textTransform: 'none',
                borderRadius: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <img
                  src={klogo}
                  alt="Print"
                  width="24"
                  height="24"
                  color="#fff"
                  style={{ marginRight: '8px' }}
                />
                <div style={{ marginLeft: '8px' }}>Havuza gönder</div>
              </div>
            </Button>
          )}
        {!order?.deliverer &&
          order?.distance >
            (meProfile?.activeBranch?.partner_plans?.[0]
              ?.maximum_delivery_distance ?? Infinity) && (
            <Typography>
              Şipariş{' '}
              {(
                (meProfile?.activeBranch?.partner_plans?.[0]
                  ?.maximum_delivery_distance ?? Infinity) / 1000
              ).toFixed()}{' '}
              km altında olmalı
            </Typography>
          )}
      </DialogActions>
    </Dialog>
  );
}

export default DelivererModal;
